// A client navigation whose request never reached us.
//
// Inertia's XHR transport rejects with `HttpNetworkError` when the browser
// reports a transport-level failure (`xhr.onerror` — a status of 0, no
// response). The request was dropped before any HTTP exchange: a filtering
// proxy or AV web-shield killing it locally, an extension blocking it, a
// dropped connection. Nothing about it is a server fault, and nothing about it
// is distinguishable from the client side.
//
// WHAT INERTIA DOES WITH IT, which is the whole reason this file exists
// (@inertiajs/core 3.6.1, dist/index.js:2902-2915):
//
//     if (error instanceof HttpCancelledError) return
//     if (this.requestParams.all().onNetworkError(error) === false) return
//     if (fireNetworkErrorEvent(error)) {
//       return Promise.reject(error)
//     }
//
// `fireNetworkErrorEvent` dispatches a CANCELABLE `inertia:networkError` on
// `document`. Cancel it and the failure stops there; leave it uncancelled and
// Inertia rethrows into a promise nobody owns — an unhandled rejection. Until
// this file, we registered nothing, so every such failure arrived in Sentry via
// `auto.browser.global_handlers.onunhandledrejection`, which is exactly how
// Sentry d1c33931 (2026-10-21, Edge 155, Sheffield) got there.
//
// THE UNHANDLED REJECTION WAS NEVER THE REAL DEFECT. Reproduced against
// production by aborting that one XHR: the URL does not change, the heading does
// not change, and there is no error UI anywhere on the page. The visitor clicked
// "Support" and NOTHING HAPPENED — no message, no spinner, no retry. They click
// again or they leave. That silence is what this fixes; taking the event out of
// the unhandled-rejection path is the lesser half.
//
// Reporting it deliberately is strictly better than catching it by accident: it
// arrives `handled`, at warning level (a dropped client request is not an app
// fault), fingerprinted as one issue, and tagged with the path and method that
// failed — none of which the global handler could give us.
import * as Sentry from '@sentry/react'

const ALERT_ID = 'jx-network-error'

// What Inertia was doing when the transport died. `inertia:networkError` carries
// only the error, so the method has to come from the `inertia:before` that
// started the request — and the method is what decides whether offering a retry
// is help or harm (see `retryable` below).
type AttemptedRequest = {
  method: string
  url: string
  prefetch: boolean
}

let lastAttempt: AttemptedRequest | null = null

// `detail.error` is typed as plain `Error`; the transport's own subclass carries
// the URL it failed on and an `ERR_NETWORK` code.
function attemptedUrl(error: Error): string | null {
  const url = (error as { url?: unknown }).url
  return typeof url === 'string' && url.length > 0 ? url : null
}

function errorCode(error: Error): string | null {
  const code = (error as { code?: unknown }).code
  return typeof code === 'string' ? code : null
}

// Only trust the recorded attempt if it is demonstrably the one that failed.
// Inertia cancels an in-flight visit when a new one starts, so last-started is
// normally the right answer — but "normally" is not good enough here, because
// getting the method wrong on a form submit costs a visitor their typed answers.
// A mismatch falls back to null, and null means no retry button.
function matchingAttempt(url: string | null): AttemptedRequest | null {
  if (url === null || lastAttempt === null) return null
  return lastAttempt.url === url ? lastAttempt : null
}

// A GET is safe to retry by loading the page again: there is nothing on screen
// to lose. ANY non-GET is not — the four Inertia `useForm` surfaces (enquiry,
// support ticket, admin login, admin TOTP) all post, and a "Try again" that
// navigated to the form's action URL would discard everything the visitor had
// typed. The enquiry form is the product; it does not get a destructive button.
function retryable(attempt: AttemptedRequest | null): boolean {
  return attempt !== null && attempt.method.toLowerCase() === 'get'
}

function dismiss(): void {
  document.getElementById(ALERT_ID)?.remove()
}

function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const element = document.createElement('button')
  element.type = 'button'
  element.className = className
  element.textContent = label
  element.addEventListener('click', onClick)
  return element
}

// Built with DOM APIs rather than innerHTML: none of this text is attacker-
// controlled today, and assembling it this way means it cannot become so.
function showAlert(attempt: AttemptedRequest | null, url: string | null): void {
  // One alert, never a stack — a flapping connection can raise several of these
  // in a row and the visitor needs one message, not a pile.
  dismiss()

  const alert = document.createElement('div')
  alert.id = ALERT_ID
  alert.className = 'jx-network-alert'
  // Announced without stealing focus. Taking focus mid-browse is hostile, and
  // the actions below stay reachable by tab.
  alert.setAttribute('role', 'alert')

  const message = document.createElement('p')
  message.className = 'jx-network-alert__message'
  // Deliberately neutral about blame. A status of 0 cannot tell us whose end
  // broke, so the copy does not claim it was the visitor's connection.
  message.textContent = retryable(attempt)
    ? "We couldn't load that page — the connection was interrupted."
    : "We couldn't send that — the connection was interrupted. Your answers are still on the page, so please try again."
  alert.append(message)

  const actions = document.createElement('div')
  actions.className = 'jx-network-alert__actions'

  if (retryable(attempt) && url !== null) {
    // A full document load, not a router visit: the XHR transport is what just
    // failed, and a fresh document is both likelier to succeed and a clean slate
    // if the client itself is wedged.
    actions.append(
      button('Try again', 'jx-network-alert__retry', () => {
        window.location.assign(url)
      }),
    )
  }

  actions.append(button('Dismiss', 'jx-network-alert__dismiss', dismiss))
  alert.append(actions)

  document.body.append(alert)
}

export function reportNavigationFailures(): void {
  if (typeof window === 'undefined') return

  document.addEventListener('inertia:before', (event) => {
    const visit = event.detail.visit
    lastAttempt = {
      method: visit.method,
      url: visit.url.href,
      prefetch: visit.prefetch,
    }
  })

  document.addEventListener('inertia:networkError', (event) => {
    // Cancel FIRST and unconditionally. This single call is what keeps the
    // failure out of Inertia's `Promise.reject`; everything after it is
    // best-effort and must not be able to undo it.
    event.preventDefault()

    try {
      const error = event.detail.error
      const url = attemptedUrl(error)
      const attempt = matchingAttempt(url)

      // A prefetch fails invisibly by design — the visitor only hovered, so
      // there is nothing to tell them and no page they were denied. Nothing in
      // app/frontend prefetches today; this guard is here so that turning
      // `prefetch` on later cannot start alarming people on hover.
      const silent = attempt?.prefetch === true

      Sentry.captureException(error, {
        level: 'warning',
        // One issue for the family. Without this, grouping is by stack frame —
        // which is the same frame every time anyway, but the fingerprint says so
        // on purpose rather than by coincidence.
        fingerprint: ['inertia-network-error'],
        tags: {
          'jx.failed_method': attempt?.method ?? 'unknown',
          // Path, not full URL: low cardinality, so it stays searchable as a tag.
          'jx.failed_path': url === null ? 'unknown' : new URL(url).pathname,
          'jx.prefetch': String(silent),
        },
        extra: {
          attempted_url: url ?? 'unknown',
          inertia_code: errorCode(error) ?? 'unknown',
          shown_to_visitor: !silent,
        },
      })

      if (!silent) showAlert(attempt, url)
    } catch (error) {
      // Same rule as lib/sentry.ts and lib/funnel.ts: reporting must never be
      // the thing that breaks the page.
      console.error('[network-error] handling failed', error)
    }
  })

  // A message about a request that has since succeeded is worse than no message.
  document.addEventListener('inertia:success', dismiss)
}
